import { t } from 'i18next';
import _ from 'lodash';
import { IEventRegistrationDetails, StaticRegistrationStepOptionLabel } from '../../../../../types/eventTypes';
import { RegistrationStepType } from '../../../../../types/eventTypes';
import { call, put, select, takeEvery, takeLatest, all } from 'redux-saga/effects';
import { createActionSetError } from '../../../actions/errorActions';
import { TAction } from '../../../actions/types/action';
import { TEventRegistrationStepFormItem, TSaveEventUserRegistrationPayload } from './types';
import { actionTypes, actions } from './modules';
import {
  eventBracketDetailsSelector,
  eventReminderListSelector,
  eventReminderListUISelector,
  eventUserRegistrationMultiRegistrationEventsSelector,
  eventUserRegistrationFormSelector,
  eventUserRegistrationMaxTeamSizeSelector,
  eventUserRegistrationMinTeamSizeSelector,
  eventUserRegistrationSelector,
  eventUserRegistrationUISelector,
  loadableBracketSelector
} from './selectors';
import {
  getEventUserRegistrationStep,
  getPreloadedEventUserRegistrationStep,
  setEventUserRegistrationSteps,
  updateEventUserRegistrationStep,
  updateRegistrationEventDataByLabel
} from '../../utils/utils';
import { DEFAULT_DYNAMIC_BRACKET_DETAILS_STATE, DEFAULT_DYNAMIC_EVENT_STATE } from '../../components/Brackets/utils';
import { commonApiServiceAsync } from '../../../services/commonApiService';
import { getInitialMultiRegistrationEvents } from '../../../../../../wowretail/src/frontend/scripts/eventUserRegistration/utils';

const {
  setRequestPending,
  loadEventUserRegistration,
  loadEventTeamList,
  loadEventReminderList,
  setEventUserRegistrationForm,
  setIsCheckingInRequestPending
} = actions;

interface IUpdateEventUserRegistrationSelectTeamStepParams {
  eventRegistrationDetails: any;
  eventRegistrationForm: any;
  eventRegistrationStepId: number;
  updateOptionChooseTeamData: any;
}

function* updateEventUserRegistrationSelectTeamStep(params: IUpdateEventUserRegistrationSelectTeamStepParams): any {
  const { eventRegistrationDetails, eventRegistrationForm, eventRegistrationStepId, updateOptionChooseTeamData } =
    params;

  const viewEventUserRegistrationResult = yield call(commonApiServiceAsync.viewEventUserRegistration, {
    slug: eventRegistrationDetails.slug
  });

  if (!viewEventUserRegistrationResult.eventRegistrationDetails) {
    throw new Error(t('common:errors.unable-to-load-data-reload'));
  }

  const preloadedEventRegistrationStep: TEventRegistrationStepFormItem | null = getPreloadedEventUserRegistrationStep({
    eventRegistrationStepId,
    eventRegistrationDetails: viewEventUserRegistrationResult.eventRegistrationDetails
  });

  const eventRegistrationStep: TEventRegistrationStepFormItem | null = getEventUserRegistrationStep({
    eventRegistrationStepId,
    eventRegistrationForm
  });

  const currentMultiRegistrationEventsState = yield select(eventUserRegistrationMultiRegistrationEventsSelector);

  yield put(
    setEventUserRegistrationForm({
      eventId: eventRegistrationDetails.id,
      slug: eventRegistrationDetails.slug,
      multiRegistrationEvents: currentMultiRegistrationEventsState,
      registrationSteps: updateEventUserRegistrationStep({
        eventRegistrationStepId,
        eventRegistrationForm: eventRegistrationForm,
        updateData: {
          ...(preloadedEventRegistrationStep?.validators
            ? { validators: preloadedEventRegistrationStep?.validators }
            : {}),
          ...(preloadedEventRegistrationStep?.preload ? { preload: preloadedEventRegistrationStep?.preload } : {}),
          ...(eventRegistrationStep?.data
            ? {
                data: updateRegistrationEventDataByLabel(
                  eventRegistrationStep?.data,
                  StaticRegistrationStepOptionLabel.CHOOSE_TEAM,
                  updateOptionChooseTeamData
                )
              }
            : {})
        }
      })
    })
  );
}

function* fetchEventUserRegistrationSaga(action: TAction<any>): any {
  try {
    yield put(
      loadEventUserRegistration({
        isLoading: true,
        isLoaded: false,
        details: null,
        ui: {}
      })
    );

    const { eventRegistrationDetails }: { eventRegistrationDetails: IEventRegistrationDetails } = yield call(
      commonApiServiceAsync.viewEventUserRegistration,
      action.payload
    );

    yield put(
      setEventUserRegistrationForm({
        eventId: eventRegistrationDetails.id,
        slug: eventRegistrationDetails.slug,
        registrationSteps: setEventUserRegistrationSteps(eventRegistrationDetails),
        multiRegistrationEvents: getInitialMultiRegistrationEvents(eventRegistrationDetails)
      })
    );

    yield put(
      loadEventUserRegistration({
        isLoading: false,
        isLoaded: true,
        details: eventRegistrationDetails,
        ui: _.pick(action.payload, 'slug', 'inviteCode')
      })
    );
  } catch (error) {
    yield put(createActionSetError({ body: error.message, type: error.statusCode, title: error.error }));
  }
}

function* fetchEventTeamListSaga(action: TAction<any>): any {
  const minTeamSize = yield select(eventUserRegistrationMinTeamSizeSelector);
  const maxTeamSize = yield select(eventUserRegistrationMaxTeamSizeSelector);

  try {
    yield put(
      loadEventTeamList({
        isLoading: true,
        isLoaded: false,
        list: [],
        ui: {
          count: 0,
          page: 0,
          pageSize: 20,
          minTeamSize: null,
          maxTeamSize: null,
          search: ''
        }
      })
    );

    const { teamList, count, page, pageSize, eventSlug, search } = yield call(
      commonApiServiceAsync.viewEventTeamList,
      action.payload
    );

    yield put(
      loadEventTeamList({
        isLoading: false,
        isLoaded: true,
        list: teamList,
        ui: {
          eventSlug,
          count,
          page,
          pageSize,
          minTeamSize,
          maxTeamSize,
          search
        }
      })
    );
  } catch (error) {
    yield put(createActionSetError({ body: error.message, type: error.statusCode, title: error.error }));
  }
}

function* fetchEventReminderListSaga(action: TAction<any>): any {
  try {
    yield put(
      loadEventReminderList({
        isLoading: true,
        isRefreshing: true,
        isLoaded: false,
        list: [],
        ui: {}
      })
    );

    const { reminderList } = yield call(commonApiServiceAsync.viewEventReminderList, action.payload);

    yield put(
      loadEventReminderList({
        isLoading: false,
        isRefreshing: false,
        isLoaded: true,
        list: reminderList,
        ui: _.pick(action.payload, ['eventSlug'])
      })
    );
  } catch (error) {
    yield put(createActionSetError({ body: error.message, type: error.statusCode, title: error.error }));
  }
}

function* refreshEventReminderListSaga(action: TAction<any>): any {
  const eventReminderList = yield select(eventReminderListSelector);
  const eventReminderListUI = yield select(eventReminderListUISelector);

  try {
    yield put(
      loadEventReminderList({
        isLoading: false,
        isRefreshing: true,
        isLoaded: true,
        list: eventReminderList,
        ui: eventReminderListUI
      })
    );

    const { reminderList } = yield call(commonApiServiceAsync.viewEventReminderList, action.payload);

    yield put(
      loadEventReminderList({
        isLoading: false,
        isRefreshing: false,
        isLoaded: true,
        list: reminderList,
        ui: _.pick(action.payload, ['eventSlug'])
      })
    );
  } catch (error) {
    // Note: Don't show any errors with reminders after refresh
    // yield put(createActionSetError({ body: error.message, type: error.statusCode, title: error.error }));
  }
}

function* fetchEventBracketListSaga(action: TAction<any>): any {
  try {
    yield put(
      actions.loadEventBracketList({
        isLoading: true,
        isLoaded: false,
        list: [],
        ui: {},
        relatedEvents: []
      })
    );

    const { brackets, relatedEvents } = yield call(commonApiServiceAsync.getEventBracketList, action.payload);

    yield put(
      actions.loadEventBracketList({
        isLoading: false,
        isLoaded: true,
        list: brackets,
        relatedEvents,
        ui: _.pick(action.payload, 'eventSlug')
      })
    );
  } catch (error) {
    yield put(createActionSetError({ body: error.message, type: error.statusCode, title: error.error }));
  }
}

function* fetchEventBracketDetailsSaga(action: TAction<any>): any {
  try {
    yield put(
      actions.loadEventBracketDetails({
        isLoading: true,
        isLoaded: false,
        details: null,
        ui: {},
        isRefreshing: false
      })
    );

    const { bracket } = yield call(commonApiServiceAsync.getEventBracketDetails, action.payload);

    yield put(
      actions.loadEventBracketDetails({
        isLoading: false,
        isLoaded: true,
        details: bracket,
        ui: _.pick(action.payload, 'eventSlug', 'bracketSlug'),
        isRefreshing: false
      })
    );
  } catch (error) {
    yield put(createActionSetError({ body: error.message, type: error.statusCode, title: error.error }));
  }
}

function* refreshEventBracketDetailsSaga(action: TAction<any>): any {
  const selectedEventBracketDetails = yield select(eventBracketDetailsSelector);

  try {
    yield put(
      actions.loadEventBracketDetails({
        ...selectedEventBracketDetails,
        isRefreshing: true
      })
    );

    const { bracket } = yield call(commonApiServiceAsync.getEventBracketDetails, action.payload);

    yield put(
      actions.loadEventBracketDetails({
        isLoading: false,
        isLoaded: true,
        details: bracket,
        ui: _.pick(action.payload, 'eventSlug', 'bracketSlug'),
        isRefreshing: false
      })
    );
  } catch (error) {
    // Public-facing bracket auto-reload isn't popping up errors if the auto refresh fails to load
    // yield put(createActionSetError({ body: error.message, type: error.statusCode, title: error.error }));
  }
}

function* saveEventUserRegistrationSaga(action: TAction<TSaveEventUserRegistrationPayload>): any {
  const eventRegistrationDetails: IEventRegistrationDetails | null = yield select(eventUserRegistrationSelector);
  const eventRegistrationDetailsUI = yield select(eventUserRegistrationUISelector);

  try {
    yield put(setRequestPending(true));

    const { registration_found, yourTeam } = yield call(
      commonApiServiceAsync.saveEventUserRegistration,
      action.payload
    );

    if (eventRegistrationDetails) {
      yield put(actions.removeRequiredRegistrationEvent(eventRegistrationDetails.id));

      yield put(
        loadEventUserRegistration({
          isLoading: false,
          isLoaded: true,
          details: {
            ...eventRegistrationDetails,
            eventInfo: { registration_found, yourTeam }
          },
          ui: eventRegistrationDetailsUI
        })
      );
    }

    const {
      eventRegistrationDetails: newEventRegistrationDetails
    }: { eventRegistrationDetails: IEventRegistrationDetails } = yield call(
      commonApiServiceAsync.viewEventUserRegistration,
      { slug: action.payload.slug }
    );

    yield put(
      loadEventUserRegistration({
        isLoading: false,
        isLoaded: true,
        details: newEventRegistrationDetails,
        ui: _.pick(action.payload, 'slug', 'inviteCode')
      })
    );

    yield put(
      loadEventTeamList({
        isLoading: false,
        isLoaded: false,
        list: [],
        ui: {
          count: 0,
          page: 0,
          pageSize: 20,
          minTeamSize: null,
          maxTeamSize: null,
          search: ''
        }
      })
    );
  } catch (error) {
    yield put(createActionSetError({ body: error.message, type: error.statusCode, title: error.error }));
  } finally {
    yield put(setRequestPending(false));
  }
}

function* cancelEventUserRegistrationSaga(action: TAction<any>): any {
  try {
    yield put(setRequestPending(true));

    yield call(commonApiServiceAsync.cancelEventUserRegistration, action.payload);

    yield put(
      loadEventUserRegistration({
        isLoading: false,
        isLoaded: false,
        details: null,
        ui: {}
      })
    );

    yield put(
      loadEventTeamList({
        isLoading: false,
        isLoaded: false,
        list: [],
        ui: {
          count: 0,
          page: 0,
          pageSize: 20,
          minTeamSize: null,
          maxTeamSize: null,
          search: ''
        }
      })
    );
  } catch (error) {
    yield put(createActionSetError({ body: error.message, type: error.statusCode, title: error.error }));
  } finally {
    yield put(setRequestPending(false));
  }
}

function* cancelEventEntriesTeamRegistrationSaga(action: TAction<any>) {
  try {
    yield put(setRequestPending(true));

    yield call(commonApiServiceAsync.cancelEventEntriesTeamRegistration, action.payload);

    yield put(
      loadEventUserRegistration({
        isLoading: false,
        isLoaded: false,
        details: null,
        ui: {}
      })
    );

    yield put(
      loadEventTeamList({
        isLoading: false,
        isLoaded: false,
        list: [],
        ui: {
          count: 0,
          page: 0,
          pageSize: 20,
          minTeamSize: null,
          maxTeamSize: null,
          search: ''
        }
      })
    );
  } catch (error) {
    yield put(createActionSetError({ body: error.message, type: error.statusCode, title: error.error }));
  } finally {
    yield put(setRequestPending(false));
  }
}

function* joinToEventTeamSaga(action: TAction<any>): any {
  const eventRegistrationDetails = yield select(eventUserRegistrationSelector);
  const eventRegistrationForm = yield select(eventUserRegistrationFormSelector);

  try {
    yield put(setRequestPending(true));

    yield updateEventUserRegistrationSelectTeamStep({
      eventRegistrationDetails,
      eventRegistrationForm,
      eventRegistrationStepId: action.payload.eventRegistrationStepId,
      updateOptionChooseTeamData: {
        value: action.payload.charterId
      }
    });
  } catch (error) {
    yield put(
      createActionSetError({
        title: t('common:errors.something-went-wrong'),
        body: error.message,
        type: 'close'
      })
    );
  } finally {
    yield put(setRequestPending(false));
  }
}

function* createEventTeamSaga(action: TAction<any>): any {
  const eventRegistrationDetails = yield select(eventUserRegistrationSelector);
  const eventRegistrationForm = yield select(eventUserRegistrationFormSelector);

  try {
    yield put(setRequestPending(true));

    const result = yield call(commonApiServiceAsync.createGroupCharter, {
      ...action.payload.values,
      region: 'us', // event teams do not consider region, but we have to set one. So just mark all event teams as US.
      namespace: eventRegistrationDetails.namespace,
      createInviteCode: true,
      eventId: eventRegistrationDetails.id
    });

    yield updateEventUserRegistrationSelectTeamStep({
      eventRegistrationDetails,
      eventRegistrationForm,
      eventRegistrationStepId: action.payload.eventRegistrationStepId,
      updateOptionChooseTeamData: {
        value: result.id
      }
    });
  } catch (error) {
    yield put(
      createActionSetError({
        title: t('common:errors.something-went-wrong'),
        body: error.message,
        type: 'close'
      })
    );
  } finally {
    yield put(setRequestPending(false));
  }
}

function* disbandEventTeamSaga(action: TAction<any>): any {
  const eventRegistrationDetails = yield select(eventUserRegistrationSelector);
  const eventRegistrationForm = yield select(eventUserRegistrationFormSelector);
  const eventRegistrationDetailsUI = yield select(eventUserRegistrationUISelector);

  try {
    yield put(setRequestPending(true));
    yield call(commonApiServiceAsync.deleteGroupCharter, { groups_charter_id: action.payload.charterId });

    yield updateEventUserRegistrationSelectTeamStep({
      eventRegistrationDetails,
      eventRegistrationForm,
      eventRegistrationStepId: action.payload.eventRegistrationStepId,
      updateOptionChooseTeamData: {
        value: ''
      }
    });

    yield put(
      loadEventUserRegistration({
        isLoading: false,
        isLoaded: true,
        details: {
          ...eventRegistrationDetails,
          eventInfo: { registration_found: null, yourTeam: null }
        },
        ui: eventRegistrationDetailsUI
      })
    );
  } catch (error) {
    yield put(
      createActionSetError({
        title: t('common:errors.something-went-wrong'),
        body: error.message,
        type: 'close'
      })
    );
  } finally {
    yield put(setRequestPending(false));
  }
}

function* leaveFromEventTeamSaga(action: TAction<any>): any {
  try {
    yield put(setRequestPending(true));
    yield call(commonApiServiceAsync.leaveGroupCharter, { groups_charter_id: action.payload.charterId });

    yield put(
      loadEventUserRegistration({
        isLoading: false,
        isLoaded: false,
        details: null,
        ui: {}
      })
    );
  } catch (error) {
    yield put(
      createActionSetError({
        title: t('common:errors.something-went-wrong'),
        body: error.message,
        type: 'close'
      })
    );
  } finally {
    yield put(setRequestPending(false));
  }
}

function* regenerateEventTeamInviteSaga(action: TAction<any>): any {
  const eventRegistrationDetails = yield select(eventUserRegistrationSelector);
  const eventRegistrationForm = yield select(eventUserRegistrationFormSelector);

  try {
    yield put(setRequestPending(true));
    yield call(commonApiServiceAsync.regenerateGroupCharterInvite, {
      charterId: action.payload.charterId,
      eventId: action.payload.eventId
    });

    if (action.payload.eventRegistrationStepId) {
      yield updateEventUserRegistrationSelectTeamStep({
        eventRegistrationDetails,
        eventRegistrationForm,
        eventRegistrationStepId: action.payload.eventRegistrationStepId,
        updateOptionChooseTeamData: {}
      });
    }
  } catch (error) {
    yield put(
      createActionSetError({
        title: t('common:errors.something-went-wrong'),
        body: error.message,
        type: 'close'
      })
    );
  } finally {
    yield put(setRequestPending(false));
  }
}

function* checkInEventBracketTeamSaga(action: TAction<any>): any {
  try {
    yield put(setRequestPending(true));
    yield put(setIsCheckingInRequestPending(true));

    const { success } = yield call(commonApiServiceAsync.checkInEventBracketTeam, action.payload);
    if (success) {
      yield call(refreshEventReminderListSaga, action);
    }
  } catch (error) {
    yield put(
      createActionSetError({
        title: t('settings:events-management.errors.title'),
        body: error.message
      })
    );
  } finally {
    yield put(setRequestPending(false));
    yield put(setIsCheckingInRequestPending(false));
  }
}

function* saveSelfReportScoresSaga(action: TAction<any>): any {
  try {
    yield put(setRequestPending(true));

    const { success } = yield call(commonApiServiceAsync.saveSelfReportScores, action.payload);
    if (success) {
      yield call(refreshEventReminderListSaga, action);
    }
  } catch (error) {
    yield put(
      createActionSetError({
        title: t('common:errors.something-went-wrong'),
        body: error.message,
        type: 'close'
      })
    );
  } finally {
    yield put(setRequestPending(false));
  }
}

function* refreshBattleAccountSaga(): any {
  const details = yield select(eventUserRegistrationSelector);
  const eventRegistrationForm = yield select(eventUserRegistrationFormSelector);

  const blizzardStep = eventRegistrationForm.registrationSteps.find(
    (step: any) => step.type === RegistrationStepType.SYNC_YOUR_BLIZZARD_ACCOUNT
  );

  if (!details || !blizzardStep) {
    return;
  }

  try {
    const { eventRegistrationDetails } = yield call(commonApiServiceAsync.viewEventUserRegistration, {
      slug: details.slug
    });

    if (!eventRegistrationDetails) {
      throw new Error(t('common:errors.unable-to-load-data-reload'));
    }

    const eventRegistrationStepId = blizzardStep.id;

    const preloadedEventRegistrationStep: TEventRegistrationStepFormItem | null = getPreloadedEventUserRegistrationStep(
      {
        eventRegistrationStepId,
        eventRegistrationDetails: eventRegistrationDetails
      }
    );

    const eventRegistrationStep: TEventRegistrationStepFormItem | null = getEventUserRegistrationStep({
      eventRegistrationStepId,
      eventRegistrationForm
    });

    const currentMultiRegistrationEventsState = yield select(eventUserRegistrationMultiRegistrationEventsSelector);

    const battleTagData = preloadedEventRegistrationStep?.data[StaticRegistrationStepOptionLabel.BATTLE_TAG];

    yield put(
      setEventUserRegistrationForm({
        eventId: eventRegistrationDetails.id,
        slug: eventRegistrationDetails.slug,
        multiRegistrationEvents: currentMultiRegistrationEventsState,
        registrationSteps: updateEventUserRegistrationStep({
          eventRegistrationStepId,
          eventRegistrationForm: eventRegistrationForm,
          updateData: {
            ...(preloadedEventRegistrationStep?.validators
              ? { validators: preloadedEventRegistrationStep?.validators }
              : {}),
            ...(preloadedEventRegistrationStep?.preload ? { preload: preloadedEventRegistrationStep?.preload } : {}),
            ...(eventRegistrationStep?.data
              ? {
                  data: updateRegistrationEventDataByLabel(
                    eventRegistrationStep?.data,
                    StaticRegistrationStepOptionLabel.BATTLE_TAG,
                    battleTagData || {}
                  )
                }
              : {})
          }
        })
      })
    );
  } catch (error) {
    yield put(
      createActionSetError({
        title: t('common:errors.something-went-wrong'),
        body: error.message,
        type: 'close'
      })
    );
  }
}

function* refreshClaimedCharactersSaga(): any {
  const details = yield select(eventUserRegistrationSelector);
  const eventRegistrationForm = yield select(eventUserRegistrationFormSelector);

  const claimCharactersStep = eventRegistrationForm.registrationSteps.find(
    (step: any) => step.type === RegistrationStepType.CLAIM_YOUR_CHARACTERS
  );

  if (!details || !claimCharactersStep) {
    return;
  }

  try {
    const { eventRegistrationDetails } = yield call(commonApiServiceAsync.viewEventUserRegistration, {
      slug: details.slug
    });

    if (!eventRegistrationDetails) {
      throw new Error(t('common:errors.unable-to-load-data-reload'));
    }

    const eventRegistrationStepId = claimCharactersStep.id;

    const preloadedEventRegistrationStep: TEventRegistrationStepFormItem | null = getPreloadedEventUserRegistrationStep(
      {
        eventRegistrationStepId,
        eventRegistrationDetails: eventRegistrationDetails
      }
    );

    const eventRegistrationStep: TEventRegistrationStepFormItem | null = getEventUserRegistrationStep({
      eventRegistrationStepId,
      eventRegistrationForm
    });

    const currentMultiRegistrationEventsState = yield select(eventUserRegistrationMultiRegistrationEventsSelector);

    const claimedCharactersData = preloadedEventRegistrationStep?.data[StaticRegistrationStepOptionLabel.CLAIMED_CHARACTERS];

    yield put(
      setEventUserRegistrationForm({
        eventId: eventRegistrationDetails.id,
        slug: eventRegistrationDetails.slug,
        multiRegistrationEvents: currentMultiRegistrationEventsState,
        registrationSteps: updateEventUserRegistrationStep({
          eventRegistrationStepId,
          eventRegistrationForm: eventRegistrationForm,
          updateData: {
            ...(preloadedEventRegistrationStep?.validators
              ? { validators: preloadedEventRegistrationStep?.validators }
              : {}),
            ...(preloadedEventRegistrationStep?.preload ? { preload: preloadedEventRegistrationStep?.preload } : {}),
            ...(eventRegistrationStep?.data
              ? {
                  data: updateRegistrationEventDataByLabel(
                    eventRegistrationStep?.data,
                    StaticRegistrationStepOptionLabel.CLAIMED_CHARACTERS,
                    claimedCharactersData || {}
                  )
                }
              : {})
          }
        })
      })
    );
  } catch (error) {
    yield put(
      createActionSetError({
        title: t('common:errors.something-went-wrong'),
        body: error.message,
        type: 'close'
      })
    );
  }
}

function* loadDynamicBracketSaga(action: TAction<{ eventSlug: string; bracketSlug: string }>): any {
  const { eventSlug, bracketSlug } = action.payload;
  const state = yield select(loadableBracketSelector);

  const eventState = state[eventSlug] || DEFAULT_DYNAMIC_EVENT_STATE;
  const bracketState = eventState.brackets[bracketSlug] || DEFAULT_DYNAMIC_BRACKET_DETAILS_STATE;

  try {
    yield put(
      actions.setDynamicBracket(eventSlug, {
        ...eventState,
        isLoading: true,
        brackets: {
          ...eventState.brackets,
          [bracketSlug]: {
            ...bracketState,
            isLoading: true
          }
        }
      })
    );

    const promises: any[] = [];
    const hasEventDetails = Boolean(eventState.event);

    if (!hasEventDetails) {
      promises.push(call(commonApiServiceAsync.getPublicEventDetails, eventSlug));
    }

    promises.push(call(commonApiServiceAsync.getEventBracketDetails, { eventSlug, bracketSlug }));

    const results = yield all(promises);

    const eventDetails = hasEventDetails ? eventState.event : results[0];
    const { bracket: bracketDetails } = results[promises.length - 1];

    yield put(
      actions.setDynamicBracket(eventSlug, {
        ...eventState,
        event: eventDetails,
        brackets: {
          ...eventState.brackets,
          [bracketSlug]: {
            ...bracketState,
            bracket: bracketDetails,
            isLoading: false,
            isLoaded: true
          }
        }
      })
    );
  } catch (error) {
    yield put(
      actions.setDynamicBracket(eventSlug, {
        ...eventState,
        isLoading: false,
        isLoaded: true,
        error: error,
        brackets: {
          ...eventState.brackets,
          [bracketSlug]: {
            ...bracketState,
            isLoading: false,
            isLoaded: true,
            error: error
          }
        }
      })
    );
  }
}

function* watchFetchEventUserRegistration(): any {
  yield takeLatest(actionTypes.VIEW_EVENT_USER_REGISTRATION, fetchEventUserRegistrationSaga);
}

function* watchFetchEventTeamList(): any {
  yield takeLatest(actionTypes.VIEW_EVENT_TEAM_LIST, fetchEventTeamListSaga);
}

function* watchFetchEventReminderList(): any {
  yield takeLatest(actionTypes.VIEW_EVENT_REMINDER_LIST, fetchEventReminderListSaga);
}

function* watchRefreshEventReminderList(): any {
  yield takeLatest(actionTypes.REFRESH_EVENT_REMINDER_LIST, refreshEventReminderListSaga);
}

function* watchFetchEventBracketList(): any {
  yield takeLatest(actionTypes.VIEW_EVENT_BRACKET_LIST, fetchEventBracketListSaga);
}

function* watchFetchEventBracketDetails(): any {
  yield takeLatest(actionTypes.VIEW_EVENT_BRACKET_DETAILS, fetchEventBracketDetailsSaga);
}

function* watchRefreshEventBracketDetails(): any {
  yield takeLatest(actionTypes.REFRESH_EVENT_BRACKET_DETAILS, refreshEventBracketDetailsSaga);
}

function* watchRequestSaveEventUserRegistration(): any {
  yield takeEvery(actionTypes.SAVE_EVENT_USER_REGISTRATION, saveEventUserRegistrationSaga);
}

function* watchRequestCancelEventUserRegistration(): any {
  yield takeEvery(actionTypes.CANCEL_EVENT_USER_REGISTRATION, cancelEventUserRegistrationSaga);
}

function* watchRequestCancelEventEntriesTeamRegistration(): any {
  yield takeEvery(actionTypes.CANCEL_EVENT_ENTRIES_TEAM_REGISTRATION, cancelEventEntriesTeamRegistrationSaga);
}

function* watchRequestJoinToEventTeam(): any {
  yield takeEvery(actionTypes.JOIN_TO_EVENT_TEAM, joinToEventTeamSaga);
}

function* watchRequestCreateEventTeam(): any {
  yield takeEvery(actionTypes.CREATE_EVENT_TEAM, createEventTeamSaga);
}

function* watchRequestDisbandEventTeam(): any {
  yield takeEvery(actionTypes.DISBAND_EVENT_TEAM, disbandEventTeamSaga);
}

function* watchRequestLeaveFromEventTeam(): any {
  yield takeEvery(actionTypes.LEAVE_FROM_EVENT_TEAM, leaveFromEventTeamSaga);
}

function* watchRequestRegenerateEventTeamInvite(): any {
  yield takeEvery(actionTypes.REGENERATE_EVENT_TEAM_INVITE, regenerateEventTeamInviteSaga);
}

function* watchRequestSaveSelfReportScores() {
  yield takeEvery(actionTypes.SAVE_SELF_REPORT_SCORES, saveSelfReportScoresSaga);
}

function* watchRequestCheckInEventBracketTeam(): any {
  yield takeEvery(actionTypes.CHECK_IN_EVENT_BRACKET_TEAM, checkInEventBracketTeamSaga);
}

function* watchRequestRefreshBattleAccount(): any {
  yield takeEvery(actionTypes.REFRESH_BATTLE_ACCOUNT, refreshBattleAccountSaga);
  yield takeEvery(actionTypes.REFRESH_CLAIMED_CHARACTERS, refreshClaimedCharactersSaga);
}

function* watchRequestLoadDynamicBracket(): any {
  yield takeEvery(actionTypes.LOAD_DYNAMIC_BRACKET, loadDynamicBracketSaga);
}

export default [
  watchFetchEventUserRegistration(),
  watchFetchEventTeamList(),
  watchFetchEventReminderList(),
  watchRefreshEventReminderList(),
  watchFetchEventBracketList(),
  watchFetchEventBracketDetails(),
  watchRefreshEventBracketDetails(),

  watchRequestSaveEventUserRegistration(),
  watchRequestCancelEventUserRegistration(),
  watchRequestCancelEventEntriesTeamRegistration(),
  watchRequestJoinToEventTeam(),
  watchRequestCreateEventTeam(),
  watchRequestDisbandEventTeam(),
  watchRequestLeaveFromEventTeam(),
  watchRequestRegenerateEventTeamInvite(),
  watchRequestSaveSelfReportScores(),
  watchRequestCheckInEventBracketTeam(),
  watchRequestRefreshBattleAccount(),
  watchRequestLoadDynamicBracket()
];
